/**
 * The Explore tab: a citation-grounded view of the patient entity graph.
 *
 * Renamed from "Wiki" to "Explore" (Phase 5a). The sub-views, data layer, and
 * component files under ``wiki/`` are unchanged — only the tab-level name and
 * user-facing copy are updated here. The Explore tab has six sub-views switched
 * via a subtab strip: Index (the page directory), Page (one entity, opened from
 * the index), Query (cited Q&A), Timeline (chronological events), Data Quality
 * (a taxonomy/consistency lint feed — internal view id "session"), and Log (the
 * ingestion-run ledger). Nothing is logged (HIPAA).
 */
import React, { useState } from "react";
import { ErrorBoundary } from "../ErrorBoundary";
import WikiIndexView from "./WikiIndexView";
import WikiPageView from "./WikiPageView";
import WikiQueryView from "./WikiQueryView";
import WikiTimelineView from "./WikiTimelineView";
import WikiSessionPrepView from "./WikiSessionPrepView";
import WikiLogView from "./WikiLogView";
import WikiViewBoundary from "./WikiViewBoundary";

/** The selectable Explore sub-views. */
type ExploreView = "index" | "query" | "timeline" | "session" | "log";

/** A subtab descriptor: its view id and label. */
interface SubTabDef {
  view: ExploreView;
  label: string;
}

const SUBTABS: SubTabDef[] = [
  { view: "index", label: "Index" },
  { view: "query", label: "Query" },
  { view: "timeline", label: "Timeline" },
  // Visible label is "Data Quality" (it is a lint/consistency feed, not a
  // clinical pre-visit briefing); the internal view id stays "session" so the
  // data-testid + e2e selectors are unchanged (F5, captain decision 2026-07-06).
  { view: "session", label: "Data Quality" },
  { view: "log", label: "Log" },
];

/** Tailwind classes for an active vs. inactive subtab button. */
function subTabClass(active: boolean): string {
  return active
    ? "px-3 py-1 rounded bg-slate-800 text-white text-sm font-medium"
    : "px-3 py-1 rounded text-slate-600 hover:bg-slate-100 text-sm";
}

/** Props for {@link ExploreTab}. */
interface ExploreTabProps {
  /** The active patient id. */
  patientId: string;
}

/** The non-page sub-views, dispatched by view id. */
function renderView(
  view: ExploreView,
  patientId: string,
  openPage: (documentId: string) => void,
): React.ReactElement {
  if (view === "query") {
    return <WikiQueryView patientId={patientId} onOpenPage={openPage} />;
  }
  if (view === "timeline") {
    return <WikiTimelineView patientId={patientId} />;
  }
  if (view === "session") {
    return <WikiSessionPrepView patientId={patientId} />;
  }
  if (view === "log") {
    return <WikiLogView patientId={patientId} />;
  }
  return <WikiIndexView patientId={patientId} onOpenPage={openPage} />;
}

/**
 * Render the Explore tab for a patient.
 *
 * @param props - The active patient id.
 * @returns The Explore tab element.
 */
export const ExploreTab: React.FC<ExploreTabProps> = ({ patientId }) => {
  const [view, setView] = useState<ExploreView>("index");
  const [pageId, setPageId] = useState<string | null>(null);

  const openPage = (documentId: string): void => {
    setPageId(documentId);
    setView("index");
  };

  const backToIndex = (): void => setPageId(null);

  const selectView = (next: ExploreView): void => {
    setPageId(null);
    setView(next);
  };

  // A selected page takes over the Index sub-view until dismissed.
  const showingPage = pageId !== null && view === "index";

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="flex items-center px-4 py-2 border-b border-slate-200 bg-white shrink-0">
        <div className="flex items-center gap-2">
          {SUBTABS.map((tab) => (
            <button
              key={tab.view}
              type="button"
              className={subTabClass(view === tab.view && !showingPage)}
              onClick={() => selectView(tab.view)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <ErrorBoundary context="explore-tab">
          {showingPage ? (
            <WikiViewBoundary view="page">
              <WikiPageView
                patientId={patientId}
                documentId={pageId as string}
                onBack={backToIndex}
                onOpenPage={openPage}
              />
            </WikiViewBoundary>
          ) : (
            <WikiViewBoundary view={view}>
              {renderView(view, patientId, openPage)}
            </WikiViewBoundary>
          )}
        </ErrorBoundary>
      </div>
    </div>
  );
};

export default ExploreTab;
